import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Keeps each "design personalisation fee" cart line tied to the specific
 * product line it was added alongside (linked via a shared
 * `_design_fee_instance_id` property). Whenever the cart changes:
 *  - removes any fee line whose linked product line is no longer present
 *    (e.g. when the customer removes a personalised item, its fee goes too)
 *  - keeps the fee line's quantity equal to its linked product line's
 *    quantity (e.g. increasing the personalised item to 4 also bumps its
 *    fee line to 4, since the fee is meant to apply per unit)
 */
class DesignFeeCleanup {
  /** Prevents overlapping cleanup requests and re-entrant loops from our own cart update. */
  #syncing = false;

  constructor() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = (event) => {
    if (event.detail?.sourceId === 'design-fee-cleanup') return;
    const cart = event.detail?.resource;
    if (cart?.items) this.#cleanup(cart);
  };

  /**
   * @param {{items: Array<{key: string, quantity: number, properties?: Record<string, string>}>}} cart
   */
  async #cleanup(cart) {
    if (this.#syncing) return;

    const mainItemsByInstanceId = new Map();
    for (const item of cart.items) {
      if (item.properties?.['Design Patch']) continue;
      const instanceId = item.properties?.['_design_fee_instance_id'];
      if (instanceId) mainItemsByInstanceId.set(instanceId, item);
    }

    const orphanedFeeLines = [];
    const quantityMismatches = [];

    for (const item of cart.items) {
      const instanceId = item.properties?.['_design_fee_instance_id'];
      if (!item.properties?.['Design Patch'] || !instanceId) continue;

      const mainItem = mainItemsByInstanceId.get(instanceId);
      if (!mainItem) {
        orphanedFeeLines.push(item);
      } else if (mainItem.quantity !== item.quantity) {
        quantityMismatches.push({ id: item.key, quantity: mainItem.quantity });
      }
    }

    if (orphanedFeeLines.length === 0 && quantityMismatches.length === 0) return;

    this.#syncing = true;
    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);
      const sections = sectionIds.join(',');

      let lastResponseData = null;
      const changes = [
        ...orphanedFeeLines.map((line) => ({ id: line.key, quantity: 0 })),
        ...quantityMismatches,
      ];
      for (const change of changes) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sections ? { ...change, sections } : change),
        });
        if (response.ok) {
          lastResponseData = await response.json();
        }
      }

      if (lastResponseData) {
        document.dispatchEvent(
          new CartUpdateEvent(lastResponseData, 'design-fee-cleanup', { sections: lastResponseData.sections })
        );
      }
    } catch {
      // A failed sync attempt is retried on the next cart update.
    } finally {
      this.#syncing = false;
    }
  }
}

new DesignFeeCleanup();
